import { io, Socket } from "socket.io-client";

export interface WebSocketMessage {
  sender: string;
  type: string;
  detail?: any;
  conversationId?: string;
  timestamp: string;
}

export interface WebSocketMessageSentResponse {
  success: boolean;
  message: string;
  failedUsers: string[];
  originalMessage: WebSocketMessage;
}

export interface WebSocketConnectionOptions {
  userId: string;
  token: string;
  onMessage?: (message: WebSocketMessage) => void;
  onMessageSent?: (response: WebSocketMessageSentResponse) => void;
  onNotification?: (notification: any) => void;
  onConnect?: () => void;
  onDisconnect?: () => void;
  onError?: (error: any) => void;
}

class WebSocketService {
  private socket: Socket | null = null;
  private isConnected = false;
  private reconnectAttempts = 0;
  private maxReconnectAttempts = 5; // Only used for auth errors
  private reconnectDelay = 1000;
  private maxReconnectDelay = 30000; // Max 30 seconds between reconnection attempts
  private hasAuthError = false;
  private messageCallbacks: Map<string, (message: WebSocketMessage) => void> = new Map();
  private messageSentCallbacks: Map<string, (response: WebSocketMessageSentResponse) => void> = new Map();
  private notificationCallbacks: Map<string, (notification: any) => void> = new Map();
  private connectCallbacks: Map<string, () => void> = new Map();
  private disconnectCallbacks: Map<string, () => void> = new Map();
  private errorCallbacks: Map<string, (error: any) => void> = new Map();
  private subscriptionCounter = 0;
  private visibilityChangeListener: (() => void) | null = null;
  private lastErrorLog: number = 0;
  private errorLogThrottle: number = 5000; // Log errors at most once every 5 seconds
  private authErrorShown: boolean = false; // Track if auth error notification was shown
  private reconnectTimer: NodeJS.Timeout | null = null; // Track reconnection timer
  private connectionOptions: WebSocketConnectionOptions | null = null; // Store connection options for reconnection
  private manualDisconnect: boolean = false; // Track if disconnect was manual

  constructor() {
    // Setup visibility change listener to reconnect when tab becomes active
    this.setupVisibilityChangeListener();
  }

  private setupVisibilityChangeListener() {
    this.visibilityChangeListener = () => {
      if (document.visibilityState === "visible") {
        // Only try to reconnect if not connected, no auth error, and not manual disconnect
        if (!this.isConnected && !this.hasAuthError && !this.manualDisconnect && this.connectionOptions) {
          console.log("👁️ Tab became visible, triggering reconnection...");
          
          // Clear any pending reconnection timer
          if (this.reconnectTimer) {
            clearTimeout(this.reconnectTimer);
            this.reconnectTimer = null;
          }
          
          // Reset reconnect attempts for immediate retry
          this.reconnectAttempts = 0;
          
          // Attempt immediate reconnection
          this.scheduleReconnection();
        } else if (this.hasAuthError) {
          console.log("🔐 Tab became visible but skipping reconnect due to auth error");
        } else if (this.isConnected) {
          console.log("✅ Tab became visible - already connected");
        }
      }
    };

    if (typeof document !== "undefined") {
      document.addEventListener("visibilitychange", this.visibilityChangeListener);
    }
  }

  connect(options: WebSocketConnectionOptions): Promise<string> {
    return new Promise((resolve, reject) => {
      try {
        // Store connection options for automatic reconnection
        this.connectionOptions = options;
        this.manualDisconnect = false;
        
        // Generate unique subscription ID
        const subscriptionId = `subscription_${Date.now()}_${++this.subscriptionCounter}`;
        
        // If already connected, just register callbacks and resolve
        if (this.socket && this.isConnected) {
          console.log("🔌 WebSocket already connected, registering callbacks with ID:", subscriptionId);
          this.registerCallbacks(subscriptionId, options);
          resolve(subscriptionId);
          return;
        }
        
        // Reset all error flags on new connection attempt
        this.hasAuthError = false;
        this.authErrorShown = false;
        this.lastErrorLog = 0;
        
        // Register callbacks with unique ID
        this.registerCallbacks(subscriptionId, options);
        
        const backendUrl = process.env.REACT_APP_API_BASE_URL || "http://localhost:3000/";
        // Clean the token to remove any extra quotes, slashes, or whitespace
        const cleanToken = options.token
          ?.replace(/^[""]|[""]$/g, "") // Remove leading/trailing quotes
          ?.replace(/^\/+|\/+$/g, "") // Remove leading/trailing slashes
          ?.trim(); // Remove whitespace
        
        
        this.socket = io(`${backendUrl}websocket`, {
          auth: {
            token: cleanToken,
            userId: options.userId,
          },
          transports: ["websocket", "polling"],
          reconnection: false, // We'll handle reconnection manually for better control
          timeout: 20000,
          autoConnect: true,
          forceNew: false,
          multiplex: true,
        });

        // Connection event handlers
        this.socket.on("connect", () => {
          console.log("🔌 WebSocket connected successfully");
          this.isConnected = true;
          this.reconnectAttempts = 0;
          
          // Clear any pending reconnection timer
          if (this.reconnectTimer) {
            clearTimeout(this.reconnectTimer);
            this.reconnectTimer = null;
          }
          
          // Call all registered connect callbacks
          this.connectCallbacks.forEach((callback, id) => {
            try {
              callback();
            } catch (error) {
              console.error(`Error in connect callback (${id}):`, error);
            }
          });
          resolve(subscriptionId);
        });

        this.socket.on("disconnect", (reason) => {
          console.log("🔌 WebSocket disconnected:", reason);
          this.isConnected = false;
          
          // Call all registered disconnect callbacks
          this.disconnectCallbacks.forEach((callback, id) => {
            try {
              callback();
            } catch (error) {
              console.error(`Error in disconnect callback (${id}):`, error);
            }
          });
          
          // Smart reconnection logic:
          // - Don't reconnect if manually disconnected or auth error
          // - For server disconnects, keep trying with exponential backoff
          const shouldReconnect = reason !== "io client disconnect" && !this.hasAuthError && !this.manualDisconnect;
          
          if (shouldReconnect) {
            console.log("🔄 Scheduling automatic reconnection...");
            this.scheduleReconnection();
          } else if (this.hasAuthError) {
            console.log("🔐 Skipping reconnection due to authentication error");
          } else if (this.manualDisconnect) {
            console.log("✋ Skipping reconnection - disconnect was manual");
          }
        });

        this.socket.on("connect_error", (error) => {
          this.isConnected = false;
          
          // Check if it's an authentication error
          const isAuth = this.isAuthError(error);
          if (isAuth) {
            this.hasAuthError = true;
            
            // Only log auth errors once every 5 seconds to prevent spam
            const now = Date.now();
            if (now - this.lastErrorLog > this.errorLogThrottle) {
              console.error("🔐 Authentication error detected, stopping reconnection attempts:", {
                message: error.message,
                details: error
              });
              this.lastErrorLog = now;
              
              // Show user-friendly notification only once
              if (!this.authErrorShown) {
                this.authErrorShown = true;
                console.warn("⚠️ Your session has expired. Please refresh the page to log in again.");
              }
            }
            
            // Clear reconnection timer
            if (this.reconnectTimer) {
              clearTimeout(this.reconnectTimer);
              this.reconnectTimer = null;
            }
            
            // Disconnect socket to stop further reconnection attempts
            if (this.socket) {
              this.socket.disconnect();
            }
          } else {
            // For non-auth errors (backend down, network issues), schedule reconnection
            const now = Date.now();
            if (now - this.lastErrorLog > this.errorLogThrottle) {
              console.error("❌ WebSocket connection error (will retry):", error.message || error);
              this.lastErrorLog = now;
            }
            
            // Schedule smart reconnection for non-auth errors
            if (!this.manualDisconnect) {
              this.scheduleReconnection();
            }
          }
          
          // Call all registered error callbacks (but only once per actual error)
          if (Date.now() - this.lastErrorLog <= 100) {
            this.errorCallbacks.forEach((callback, id) => {
              try {
                callback(error);
              } catch (callbackError) {
                console.error(`Error in error callback (${id}):`, callbackError);
              }
            });
          }
          reject(error);
        });

        // Message event handlers
        this.socket.on("message", (message: WebSocketMessage) => {
          console.log("📨 Received WebSocket message:", message);
          console.log(`📊 [WebSocketService] Total registered message callbacks: ${this.messageCallbacks.size}`);
          // Call all registered message callbacks
          this.messageCallbacks.forEach((callback, id) => {
            try {
              console.log(`📤 [WebSocketService] Calling message callback: ${id}`);
              callback(message);
            } catch (error) {
              console.error(`Error in message callback (${id}):`, error);
            }
          });
        });

        this.socket.on("messageSent", (response: WebSocketMessageSentResponse) => {
          console.log("✅ Message sent confirmation:", response);
          // Call all registered message sent callbacks
          this.messageSentCallbacks.forEach((callback, id) => {
            try {
              callback(response);
            } catch (error) {
              console.error(`Error in message sent callback (${id}):`, error);
            }
          });
        });

        // Notification event handler
        this.socket.on("notification", (notification: any) => {
          console.log("🔔 Received notification:", notification);
          console.log(`📊 [WebSocketService] Total registered notification callbacks: ${this.notificationCallbacks.size}`);
          // Call all registered notification callbacks
          this.notificationCallbacks.forEach((callback, id) => {
            try {
              console.log(`📤 [WebSocketService] Calling notification callback: ${id}`);
              callback(notification);
            } catch (error) {
              console.error(`Error in notification callback (${id}):`, error);
            }
          });
        });

        this.socket.on("error", (error) => {
          // Throttle error logging to prevent console spam
          const now = Date.now();
          if (now - this.lastErrorLog > this.errorLogThrottle) {
            console.error("❌ WebSocket error:", error);
            this.lastErrorLog = now;
            
            // Call all registered error callbacks (but only when we log)
            this.errorCallbacks.forEach((callback, id) => {
              try {
                callback(error);
              } catch (callbackError) {
                console.error(`Error in error callback (${id}):`, callbackError);
              }
            });
          }
        });

        // Reconnection event listeners
        this.socket.on("reconnect_attempt", (attemptNumber) => {
          console.log(`🔄 WebSocket reconnection attempt #${attemptNumber}`);
        });

        this.socket.on("reconnect", (attemptNumber) => {
          console.log(`✅ WebSocket reconnected after ${attemptNumber} attempts`);
          this.isConnected = true;
          this.reconnectAttempts = 0;
        });

        this.socket.on("reconnect_failed", () => {
          console.error("❌ WebSocket reconnection failed after all attempts");
        });

        this.socket.on("reconnect_error", (error) => {
          // Check if it's an auth error
          if (this.isAuthError(error)) {
            this.hasAuthError = true;
            
            // Only log once to prevent spam
            const now = Date.now();
            if (now - this.lastErrorLog > this.errorLogThrottle) {
              console.error("🔐 WebSocket reconnection auth error - stopping attempts");
              this.lastErrorLog = now;
            }
            
            // Clear reconnection timer
            if (this.reconnectTimer) {
              clearTimeout(this.reconnectTimer);
              this.reconnectTimer = null;
            }
            
            // Disconnect to stop reconnection attempts
            if (this.socket) {
              this.socket.disconnect();
            }
          } else {
            // For non-auth reconnection errors, log but continue with smart reconnection
            const now = Date.now();
            if (now - this.lastErrorLog > this.errorLogThrottle) {
              console.error("❌ WebSocket reconnection error (will retry):", error.message || error);
              this.lastErrorLog = now;
            }
          }
        });

        // Heartbeat/ping-pong to keep connection alive (silent - reduces console noise)

      } catch (error) {
        console.error("❌ Failed to initialize WebSocket connection:", error);
        reject(error);
      }
    });
  }

  private isAuthError(error: any): boolean {
    const errorMessage = error?.message || error?.description || error?.toString() || "";
    const authErrorKeywords = [
      "invalid token",
      "expired token", 
      "unauthorized",
      "authentication failed",
      "invalid or expired token",
      "token expired",
      "jwt expired",
      "jwt malformed",
      "invalid signature"
    ];
    
    return authErrorKeywords.some(keyword => 
      errorMessage.toLowerCase().includes(keyword.toLowerCase())
    );
  }

  /**
   * Schedule smart reconnection with exponential backoff
   * - Retries indefinitely for non-auth errors (server down, network issues)
   * - Uses exponential backoff: 1s, 2s, 4s, 8s, 16s, 30s (max)
   * - Logs throttled to prevent console spam
   */
  private scheduleReconnection() {
    // Clear any existing reconnection timer
    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }
    
    // Don't reconnect if manual disconnect or auth error
    if (this.manualDisconnect || this.hasAuthError || !this.connectionOptions) {
      return;
    }
    
    // Calculate delay with exponential backoff, capped at maxReconnectDelay
    this.reconnectAttempts++;
    const exponentialDelay = this.reconnectDelay * Math.pow(2, this.reconnectAttempts - 1);
    const delay = Math.min(exponentialDelay, this.maxReconnectDelay);
    
    // Log reconnection attempt (throttled)
    const now = Date.now();
    if (now - this.lastErrorLog > this.errorLogThrottle) {
      console.log(`🔄 Scheduling reconnection attempt #${this.reconnectAttempts} in ${delay}ms...`);
      this.lastErrorLog = now;
    }
    
    // Schedule reconnection
    this.reconnectTimer = setTimeout(() => {
      if (this.manualDisconnect || this.hasAuthError) {
        console.log("⏹️ Reconnection cancelled");
        return;
      }
      
      console.log(`🔌 Attempting to reconnect (attempt #${this.reconnectAttempts})...`);
      
      // Disconnect old socket if exists
      if (this.socket) {
        this.socket.removeAllListeners();
        this.socket.disconnect();
        this.socket = null;
      }
      
      // Attempt to reconnect
      this.connect(this.connectionOptions!).catch((error) => {
        // Error is already handled in connect_error handler
        // which will call scheduleReconnection again
      });
    }, delay);
  }

  sendMessage(messageData: {
    receivers: string[];
    sender: string;
    type: string;
    detail?: any;
    conversationId?: string;
  }): Promise<WebSocketMessageSentResponse> {
    return new Promise((resolve, reject) => {
      if (!this.socket || !this.isConnected) {
        reject(new Error("WebSocket is not connected"));
        return;
      }

      const message = {
        ...messageData,
        timestamp: new Date().toISOString(),
      };

      this.socket.emit("sendMessage", message, (response: WebSocketMessageSentResponse) => {
        if (response.success) {
          resolve(response);
        } else {
          reject(new Error(response.message || "Failed to send message"));
        }
      });
    });
  }

  joinUserRoom(userId: string): void {
    if (!this.socket || !this.isConnected) {
      console.warn("⚠️ Cannot join room: WebSocket is not connected");
      return;
    }

    this.socket.emit("joinUserRoom", { userId });
    console.log(`🏠 Joined user room for user: ${userId}`);
  }

  disconnect(): void {
    // Mark as manual disconnect to prevent auto-reconnection
    this.manualDisconnect = true;
    
    // Clear reconnection timer
    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }
    
    if (this.socket) {
      this.socket.disconnect();
      this.socket = null;
      this.isConnected = false;
      this.hasAuthError = false; // Reset auth error flag on manual disconnect
      this.authErrorShown = false; // Reset auth error notification flag
      this.reconnectAttempts = 0; // Reset reconnect attempts
      this.lastErrorLog = 0; // Reset error log throttle
      this.connectionOptions = null; // Clear stored connection options
      console.log("🔌 WebSocket disconnected manually");
    }

    // Clean up visibility change listener if needed
    if (this.visibilityChangeListener && typeof document !== "undefined") {
      document.removeEventListener("visibilitychange", this.visibilityChangeListener);
      this.visibilityChangeListener = null;
    }
  }

  getConnectionStatus(): boolean {
    return this.isConnected;
  }

  /**
   * Reconnect with new token after token refresh
   * This should be called when the auth token is refreshed
   */
  async reconnectWithNewToken(newToken: string, userId: string): Promise<void> {
    if (!this.connectionOptions) {
      console.warn("⚠️ Cannot reconnect: No stored connection options");
      return;
    }

    console.log("🔄 Token refreshed, reconnecting WebSocket with new token...");

    // Clear auth error flags since we have a new token
    this.hasAuthError = false;
    this.authErrorShown = false;
    this.lastErrorLog = 0;

    // Clear reconnection timer if any
    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }

    // Disconnect current socket if exists
    if (this.socket) {
      this.socket.removeAllListeners();
      this.socket.disconnect();
      this.socket = null;
    }

    // Update connection options with new token
    this.connectionOptions = {
      ...this.connectionOptions,
      token: newToken,
      userId: userId
    };

    // Reset reconnect attempts
    this.reconnectAttempts = 0;
    this.isConnected = false;

    // Attempt to connect with new token
    try {
      await this.connect(this.connectionOptions);
      console.log("✅ WebSocket reconnected successfully with new token");
    } catch (error) {
      console.error("❌ Failed to reconnect with new token:", error);
      // If connection fails, smart reconnection will take over
    }
  }

  // Helper method to register callbacks with unique ID
  private registerCallbacks(id: string, options: WebSocketConnectionOptions): void {
    console.log(`📝 [WebSocketService] Registering callbacks with ID: ${id}`);
    if (options.onMessage) {
      this.messageCallbacks.set(id, options.onMessage);
      console.log(`  ✅ Registered onMessage callback (total: ${this.messageCallbacks.size})`);
    }
    if (options.onMessageSent) {
      this.messageSentCallbacks.set(id, options.onMessageSent);
    }
    if (options.onNotification) {
      this.notificationCallbacks.set(id, options.onNotification);
    }
    if (options.onConnect) {
      this.connectCallbacks.set(id, options.onConnect);
    }
    if (options.onDisconnect) {
      this.disconnectCallbacks.set(id, options.onDisconnect);
    }
    if (options.onError) {
      this.errorCallbacks.set(id, options.onError);
    }
  }

  // Method to remove callbacks by subscription ID
  removeCallbacksById(subscriptionId: string): void {
    console.log(`🗑️ [WebSocketService] Removing callbacks with ID: ${subscriptionId} (current count: ${this.messageCallbacks.size})`);
    this.messageCallbacks.delete(subscriptionId);
    this.messageSentCallbacks.delete(subscriptionId);
    this.notificationCallbacks.delete(subscriptionId);
    this.connectCallbacks.delete(subscriptionId);
    this.disconnectCallbacks.delete(subscriptionId);
    this.errorCallbacks.delete(subscriptionId);
    console.log(`  ✅ Removed callbacks (remaining message callbacks: ${this.messageCallbacks.size})`);
  }

  // Method to remove callbacks when components unmount (legacy support)
  removeCallbacks(options: Partial<WebSocketConnectionOptions>): void {
    // Find and remove callbacks by matching the function reference
    if (options.onMessage) {
      const entries = Array.from(this.messageCallbacks.entries());
      for (const [id, callback] of entries) {
        if (callback === options.onMessage) {
          this.messageCallbacks.delete(id);
          break;
        }
      }
    }
    if (options.onMessageSent) {
      const entries = Array.from(this.messageSentCallbacks.entries());
      for (const [id, callback] of entries) {
        if (callback === options.onMessageSent) {
          this.messageSentCallbacks.delete(id);
          break;
        }
      }
    }
    if (options.onNotification) {
      const entries = Array.from(this.notificationCallbacks.entries());
      for (const [id, callback] of entries) {
        if (callback === options.onNotification) {
          this.notificationCallbacks.delete(id);
          break;
        }
      }
    }
    if (options.onConnect) {
      const entries = Array.from(this.connectCallbacks.entries());
      for (const [id, callback] of entries) {
        if (callback === options.onConnect) {
          this.connectCallbacks.delete(id);
          break;
        }
      }
    }
    if (options.onDisconnect) {
      const entries = Array.from(this.disconnectCallbacks.entries());
      for (const [id, callback] of entries) {
        if (callback === options.onDisconnect) {
          this.disconnectCallbacks.delete(id);
          break;
        }
      }
    }
    if (options.onError) {
      const entries = Array.from(this.errorCallbacks.entries());
      for (const [id, callback] of entries) {
        if (callback === options.onError) {
          this.errorCallbacks.delete(id);
          break;
        }
      }
    }
  }
}

// Export a singleton instance
export const webSocketService = new WebSocketService();
export default webSocketService;





